import React, { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { formatCompanyTypes } from "@pages/vendor-license-request/constants";
import type { CurrentLicensesSearchResponse } from "@services/licensing/types";
import { searchCurrentLicenses } from "@services/licensingApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { useTranslation } from "react-i18next";
import {
  formatDateTime,
  formatLicenseType,
  formatStatus,
  getLicenseBadgeType,
  getLicenseTypeUrlValue,
  LICENSES_FIELDS,
} from "./utils";

const LANG_BASE = "licensing";
const LANG_BASE_HEADER = "licensing.licenses-table.headers";
const TEST_ID_BASE = "licenses";

const LOCAL_STORAGE_PAGE_KEY = "licenses-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "licenses-page-size";
const CONSULT_INQUIRY_ID = "licensing.current-licenses.consult";
const CONSULT_INQUIRY_CONTEXT = {
  module: "licensing",
  screen: "Current Licenses",
  action: "consult_current_licenses",
  entityType: "license",
};

const buildDetailsNavigationParam = (id: string, licenseType?: string) => {
  if (!licenseType) return id;
  return `${id}?licenseType=${encodeURIComponent(getLicenseTypeUrlValue(licenseType))}`;
};

interface LicensesProps {
  onIsLoading: (v: boolean) => void;
  onCanRequestPortOperatorLicenseChange?: (v: boolean) => void;
}

export const Licenses: React.FC<LicensesProps> = ({
  onCanRequestPortOperatorLicenseChange,
  onIsLoading,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 10;
  });

  const {
    data,
    isLoading: isLicensesLoading,
    isFetching: isLicensesFetching,
    isError: isLicensesError,
    isSuccess: isLicensesSuccess,
    dataUpdatedAt: licensesDataUpdatedAt,
    error,
  } = useQuery<CurrentLicensesSearchResponse>({
    queryKey: ["currentLicenses", page, pageSize],
    queryFn: () =>
      searchCurrentLicenses({
        pageNumber: page - 1,
        pageSize,
      }),
    refetchOnMount: true,
  });
  useTrackPendingInquiry(CONSULT_INQUIRY_ID, {
    isFetching: isLicensesFetching,
    isLoading: isLicensesLoading,
    isError: isLicensesError,
    isSuccess: isLicensesSuccess,
    error,
    dataUpdatedAt: licensesDataUpdatedAt,
  });
  useEffect(() => {
    onIsLoading(isLicensesLoading);

    return () => onIsLoading(false);
  }, [onIsLoading, isLicensesLoading]);

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  React.useEffect(() => {
    if (isLicensesLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Licenses");
    trackScreenLoad("Licenses", "Current Licenses", startedAt.current);
  }, [error, isLicensesLoading]);

  const headers: Header[] = useMemo(
    () =>
      Object.values(LICENSES_FIELDS).map((field) => ({
        field,
        columnName: t(`${LANG_BASE_HEADER}.${field}`),
      })),
    [t],
  );
  const items = data?.items ?? [];

  React.useEffect(() => {
    const portOperatorItems = items.filter((x) => x.licenseType === "PORT_OPERATOR_LICENSE");

    const hasAnyPortOperatorRequest = portOperatorItems.length > 0;

    const allPortOperatorRejected =
      hasAnyPortOperatorRequest &&
      portOperatorItems.every(
        (x) =>
          x.status === "REJECTED_BY_MANAGING_DIRECTOR" ||
          x.status === "REJECTED_MANAGING_DIRECTOR_VERIFICATION",
      );

    const hasVendorApproved = items.some(
      (x) => x.licenseType === "VENDOR_LICENSE" && x.status === "APPROVED",
    );

    const canRequestPortOperatorLicense =
      hasVendorApproved && (!hasAnyPortOperatorRequest || allPortOperatorRejected);

    onCanRequestPortOperatorLicenseChange?.(canRequestPortOperatorLicense);
  }, [items, onCanRequestPortOperatorLicenseChange]);

  const formattedFormData = items.map((input) => {
    const na = "-";

    return {
      id: input.id,
      detailsNavigationParam: buildDetailsNavigationParam(input.id, input.licenseType),
      requestedId: input.refOrderNumber ?? na,
      licenseType: formatLicenseType(input.licenseType!) ?? na,
      companyType: formatCompanyTypes(input.companyTypes) ?? na,
      natureOfBusiness: input.natureOfBusinessInPof ?? na,
      expiredDate:
        (input.expiryDate ?? input.tradingLicenseValidityDate)
          ? formatDateTime(input.expiryDate ?? input.tradingLicenseValidityDate)
          : na,
      submittedDate: input.createdAt ? formatDateTime(input.createdAt) : na,
      tradeLicense: input.tradingLicenseNumber ?? na,
      status: formatStatus(input.status, t) ?? na,
      badgeType: getLicenseBadgeType(input.status),
    };
  });

  // NEEDED FOR EXISTING COMPONENT
  const filters: FilterItem[] = [];
  const availableFields: Record<string, FilterFieldDefinition> = {};
  const onApplyFilters = () => {};
  const onClearFilters = () => {};

  if (error) {
    return <Snackbar open autoHideDuration={6000} message={error.message} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      <CustomDataTable
        testId={TEST_ID_BASE}
        actionName={t(`${LANG_BASE}.request-port-operator-license`)}
        headers={headers}
        rows={formattedFormData}
        page={page}
        title={" "}
        pageSize={pageSize}
        totalCount={data?.totalElements ?? 0}
        canPreview={true}
        onPageChange={(newPage, newSize) => {
          if (newPage !== page || newSize !== pageSize) {
            startInquiryTiming(CONSULT_INQUIRY_ID, CONSULT_INQUIRY_CONTEXT);
          }
          setPage(newPage);
          setPageSize(newSize);
          localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
          localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
        }}
        navigationLinkBase="license-details"
        navigationParam="detailsNavigationParam"
        pageSizeOptions={[5, 10, 15, 20, 25]}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        availableFields={availableFields}
        hideFilters={true}
      />
    </div>
  );
};

export default Licenses;
